import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusColor = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  fulfilled: "bg-slate-100 text-slate-800 border-slate-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/account/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-2xl text-slate-900 mb-4">Sign in to view your account</p>
        <Link to="/login" className="px-6 py-3 bg-emerald-900 text-white text-sm rounded-sm">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <p className="overline text-emerald-800 mb-2">Account</p>
      <h1 className="font-display text-4xl font-black text-slate-900 tracking-tight">
        Hello, {user.name.split(" ")[0]}.
      </h1>
      <p className="text-slate-500 mt-2 text-sm">{user.email} {user.company && `· ${user.company}`}</p>

      <div className="mt-12">
        <p className="overline text-slate-500 mb-4">Order history</p>
        {loading ? (
          <div className="h-40 bg-slate-100 animate-pulse rounded-sm" />
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-16 text-center">
            <Package className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No orders yet.</p>
            <Link to="/shop" className="mt-4 inline-block px-5 py-2.5 bg-emerald-900 text-white text-sm rounded-sm">
              Start shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="border border-slate-200 rounded-sm bg-white overflow-hidden">
                <div className="flex flex-wrap gap-4 items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500">Order</p>
                    <p className="font-mono text-xs text-slate-900">{o.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Placed</p>
                    <p className="text-sm text-slate-900">{new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-display font-bold text-slate-900">{formatMoney(o.total)}</p>
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-sm border ${statusColor[o.status] || statusColor.pending}`}>
                    {o.status}
                  </span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {o.items.map((it) => (
                    <li key={it.product_id + it.sku} className="p-4 flex gap-3">
                      <div className="w-14 h-14 bg-slate-50 border border-slate-200 rounded-sm overflow-hidden">
                        <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900 line-clamp-1">{it.name}</p>
                        <p className="text-xs text-slate-500">
                          Qty {it.quantity} · SKU {it.sku}
                        </p>
                      </div>
                      <p className="text-sm text-slate-900 font-medium">{formatMoney(it.line_total)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
